/**
 * The component set from plan §3.6 — "build exactly these; no others".
 *
 * Sizes and colours come from the mockup HTML in `/design/mockups`, which is
 * the visual source of truth. Where a value looks oddly specific (30px button,
 * 12.5px label, 1.5px badge border) it is because the mockup uses it.
 */
import type { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import type { CardDensity } from '../lib/use-strip.js'

// --- Button --------------------------------------------------------------

type ButtonVariant = 'default' | 'primary' | 'suggest' | 'ghost' | 'danger' | 'alert'
  | 'pressed'

/**
 * `toolbar` is 36px: the list toolbar's own row. §3.6's 32px stays the default
 * everywhere else — it is the row of controls above a list that reads small
 * beside the primary action, not buttons in general.
 */
type ButtonSize = 'default' | 'big' | 'toolbar'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /**
   * Icon only: a square of the size's own height, with no side padding.
   *
   * It is a prop rather than `className="w-9 px-0"` because `cn` **joins**.
   * Both `px-3` and `px-0` landed on the button, CSS source order picked
   * `px-3`, and a 36px square with 12px of padding each side left its glyph a
   * 10px content box to live in. The icon has `flex-shrink: 1` by default, so
   * it did not overflow — it squashed, and the gear (the only icon-only
   * button on a desktop toolbar) painted about 60% as wide as the funnel
   * beside it. On a phone, where Filter is icon-only too, both did.
   *
   * So the padding is decided in one place, by the size expression, and there
   * is never a second `px-*` to lose to.
   */
  square?: boolean
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  default: 'bg-surface border-line text-text hover:bg-hover',
  primary: 'bg-accent border-accent text-white hover:bg-accent-dark',
  /*
   * Tinted, not solid: a button that does something clever rather than the
   * one thing this screen is for. Smart import reads a spreadsheet and
   * guesses what its columns mean, which is worth pointing at — but it is
   * not "New material", and two solid green buttons on one toolbar means
   * neither of them is the primary one (`packages-catalog.html`).
   */
  suggest: 'bg-green-band-bg border-accent-line text-accent-dark hover:bg-accent-soft',
  ghost: 'bg-transparent border-transparent text-text-muted hover:bg-hover',
  /** A toggle that is currently on — the Filter and Group buttons when open. */
  pressed: 'bg-surface-alt border-line-strong text-text hover:bg-hover',
  danger: 'bg-surface border-line text-danger hover:bg-danger-bg',
  /*
   * Solid red, the way `primary` is solid green: a choice somebody has made
   * that the screen should not be quiet about. The quick check's "Issue" is
   * the one that needs it — a crew glancing down the list has to see which
   * rows they flagged from arm's length.
   */
  alert: 'bg-danger border-danger text-white hover:bg-danger',
}

export function Button({
  variant = 'default', size = 'default', square, className, ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      className={buttonClass({ variant, size, square, className })}
      {...props}
    />
  )
}

/**
 * `Button`'s look as a class string, for a control that has to *be* a
 * different element and still look like the button beside it — the trigger of
 * a `MenuButton` (step 22 item 2.4). One definition, so the two cannot drift.
 */
export function buttonClass({
  variant = 'default', size = 'default', square, className,
}: Pick<ButtonProps, 'variant' | 'size' | 'square' | 'className'> = {}): string {
  return cn(
    'inline-flex items-center justify-center gap-1.5 rounded-button border font-semibold',
    'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
    // 44px is the touch target minimum the accessibility pass checks
    // (addendum §I.6).
    // 32px, which is what §3.6 actually specifies — it had drifted to 30
    // and left the toolbar's buttons two pixels shorter than the search box
    // beside them.
    /*
     * The radius belongs to the size. `radius.button` is 8px — what the
     * mockups draw on a 30–32px button and what AGENTS §3.3 always said;
     * the 10px the token used to carry is the 36px toolbar button's, so
     * that one names its own token rather than bending the shared one.
     */
    size === 'big'
      ? cn('h-11 text-body rounded-touch', square ? 'w-11 px-0' : 'px-4')
      : size === 'toolbar'
        ? cn('h-9 text-[12.5px] rounded-button-toolbar', square ? 'w-9 px-0' : 'px-3')
        : cn('h-8 text-[12px]', square ? 'w-8 px-0' : 'px-2.5'),
    BUTTON_VARIANTS[variant],
    className,
  )
}

// --- Chip, Tag, StatusChip ----------------------------------------------

export interface ChipProps extends HTMLAttributes<HTMLButtonElement> {
  active?: boolean
  /** The muted key label, e.g. "Status" in "Status: Scheduled". */
  label?: string
  /**
   * `control` is the 30px chip a filter control is built from — the status
   * values, the date presets (`30-list-filters/html/filters-02-kinds.html`).
   *
   * Bigger than the 27px chip the toolbar wears, and deliberately: a toolbar
   * chip is a label with an × on it, and these are the things you click
   * twenty of while deciding what you are looking at.
   */
  size?: 'default' | 'control'
}

/** Filter chip — 26–28px, pill, green when active (plan §3.5). */
export function Chip({
  active = false, label, children, className, size = 'default', ...props
}: ChipProps) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-chip border font-semibold',
        size === 'control' ? 'h-[30px] px-2.5 text-cell' : 'h-[27px] px-2.5 text-chip',
        active
          ? 'border-accent-line bg-accent-soft text-accent'
          : 'border-line bg-surface text-text hover:bg-hover',
        className,
      )}
      {...props}
    >
      {label && <span className="font-normal text-text-faint">{label}</span>}
      {children}
    </button>
  )
}

/** 18px, 4px radius (plan §3.6). */
/**
 * `tone` rather than colours passed through `className`, for the reason given
 * on `Card`: both classes land on the element and the stylesheet decides.
 * `bg-line-soft` is emitted after every tint, so every coloured tag in the
 * product — Overdue, Conflict, Ready — came out the same grey, and the base
 * carried no `border` width either, so the border colours did nothing.
 */
export type TagTone = 'plain' | 'quiet' | 'ok' | 'info' | 'warn' | 'bad' | 'dark'

const TAG_TONES: Record<TagTone, string> = {
  plain: 'border-transparent bg-line-soft text-text-muted',
  quiet: 'border-line bg-surface-alt text-text-muted',
  ok: 'border-accent-line bg-accent-soft text-accent-dark',
  info: 'border-info bg-info-bg text-text',
  warn: 'border-warn bg-warn-bg text-text',
  bad: 'border-danger bg-danger-bg text-danger',
  /*
   * Rowted's own, never a customer's — the ADMIN chip on an environment row
   * and the console's tile (`identity-and-sign-in.md` board 1). It reads as a
   * different product rather than a different status, which is the point.
   */
  dark: 'border-text bg-text text-white',
}

export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: TagTone
}

export function Tag({ tone = 'plain', children, className, ...props }: TagProps) {
  return (
    <span
      className={cn(
        'inline-flex h-[18px] items-center whitespace-nowrap rounded-tag border px-1.5 text-tag',
        TAG_TONES[tone],
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}

/**
 * Status chip — 22px, a dot plus a label in one of the semantic pairs from
 * plan §3.1. The pair is looked up from the CSS variables the token build
 * emits, so a status colour is never hand-typed in a component.
 */
export interface StatusChipProps {
  group: 'job' | 'invoice' | 'equipment' | 'demand' | 'po' | 'serviceLine' | 'material' | 'receipt'
    | 'vendorRental'
    /** The visit page's own chips (`docs/visit-page.md` §17.1, §7.4, §7.3) — Slice 1. */
    | 'visit' | 'visitDay' | 'visitPo'
  status: string
  children?: ReactNode
  className?: string
}

export function StatusChip({ group, status, children, className }: StatusChipProps) {
  const key = `--status-${group}-${status}`
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center gap-1.5 rounded-chip px-2 text-chip font-semibold',
        className,
      )}
      style={{
        color: `rgb(var(${key}-fg))`,
        background: `rgb(var(${key}-bg))`,
      }}
    >
      <span
        aria-hidden
        className="h-[6px] w-[6px] rounded-full"
        style={{ background: `rgb(var(${key}-fg))` }}
      />
      {children ?? humanize(status)}
    </span>
  )
}

/** "in_progress" → "In progress". Plan §3.8: sentence case everywhere. */
export function humanize(value: string): string {
  const spaced = value.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

// --- Card, labels, metric card ------------------------------------------

/**
 * `tone` rather than a `className` override, because an override does not
 * reliably win. Both classes end up on the element and the stylesheet decides:
 * `bg-surface` is emitted after `bg-danger-bg`, so a card passed
 * "border-danger bg-danger-bg" came out plain white and nothing said so. The
 * `-edge` tones colour the border and leave the background alone, which is
 * what a whole card of rows wants.
 */
export type CardTone = 'plain' | 'ok' | 'warn' | 'bad' | 'warn-edge' | 'bad-edge'

const CARD_TONES: Record<CardTone, string> = {
  plain: 'border-line bg-surface',
  ok: 'border-accent-line bg-accent-soft',
  warn: 'border-warn bg-warn-bg',
  bad: 'border-danger bg-danger-bg',
  'warn-edge': 'border-warn bg-surface',
  'bad-edge': 'border-danger bg-surface',
}

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: CardTone
}

export function Card({ tone = 'plain', className, ...props }: CardProps) {
  return (
    <div
      className={cn('rounded-card border', CARD_TONES[tone], className)}
      {...props}
    />
  )
}

/** `.h4` — 10.5px, 600, uppercase, 0.06em, faint (mockup CSS). */
export function SectionLabel({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn('text-label uppercase text-text-faint', className)} {...props} />
}

export interface MetricCardProps {
  label: string
  value: string
  sub?: string
  /** The dot beside the label; a semantic pair or any CSS colour. */
  dotColor?: string
  selected?: boolean
  onClick?: () => void
  /**
   * How much card there is room for — see `lib/use-strip.ts`. A metric card
   * sits in the same non-wrapping row as the stat cards and gives way on the
   * same ladder, so the row degrades as one thing rather than in two halves.
   */
  density?: CardDensity
}

/**
 * Metric card — plan §3.5: every metric card is a saved filter and is
 * selectable, showing `accent.soft` with an accent border and ring when on.
 */
export function MetricCard({
  label, value, sub, dotColor, selected = false, onClick, density = 'full',
}: MetricCardProps) {
  // A metric card has no chart, so `flat` is the same card as `compact`; at
  // `chip` the label and the figure go on one line and the sub line goes.
  const inline = density === 'chip'
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      title={label}
      className={cn(
        'flex h-full w-full rounded-card border text-left transition-colors',
        inline
          ? 'items-center gap-2 px-2.5 py-2'
          : 'flex-col items-start gap-1 px-3 py-2.5',
        density === 'full' && 'min-w-[148px]',
        selected
          ? 'border-accent-line bg-accent-soft shadow-[0_0_0_2px_rgb(var(--color-accent-ring))]'
          : 'border-line bg-surface hover:bg-hover',
      )}
    >
      <span
        className={cn(
          'flex items-center gap-1.5 text-[11.5px] font-semibold text-text-muted',
          inline && 'min-w-0 flex-1',
        )}
      >
        {dotColor && (
          <span aria-hidden className="h-[7px] w-[7px] shrink-0 rounded-full"
            style={{ background: dotColor }} />
        )}
        <span className="truncate">{label}</span>
      </span>
      <span className={cn('tnum text-kpi text-text', inline && 'shrink-0')}>{value}</span>
      {sub && !inline && <span className="truncate text-sub text-text-faint">{sub}</span>}
    </button>
  )
}

// --- Avatar, Switch, Checkbox, Segmented --------------------------------

export interface AvatarProps {
  /** A photo to show instead of the initials. */
  src?: string
  name: string
  size?: number
  /** The current user gets the accent ring colour (mockup: "AK" in green). */
  self?: boolean
  /**
   * `accent` for a circle that stands for the company rather than a person —
   * the tenant row on the settings home. Left alone, a name is hashed into one
   * of the seven person colours, which is meaningless for a company.
   */
  tone?: 'auto' | 'accent'
  className?: string
}

/**
 * The colours an initials circle can be.
 *
 * The job lifecycle's own pairs, and nothing new. §3.1 lists the semantic
 * text/background pairs the product is allowed to use, the token pipeline
 * derives each one's dark variant and checks its contrast, and a hex typed
 * here would have neither — it would look fine in the light and be unreadable
 * at six in the morning in a truck.
 *
 * Seven of the ten, chosen for what they are *not*: grey is the colour the
 * circle already was, and red and the terminal greys mean something is wrong
 * everywhere else in Rowted. A person is not a problem.
 */
const AVATAR_TONES = [
  'reviewed', 'quoted', 'approved', 'scheduled', 'in_progress', 'completed', 'invoiced',
] as const

/**
 * The same person is the same colour, forever.
 *
 * From the name rather than the id, so the circle beside "Rita Alvarez" is the
 * same on the list, on her record and on a job she is the contact for — the
 * id is not always to hand, and a colour that changed between two screens
 * would be worse than no colour at all.
 */
function avatarTone(name: string): string {
  let hash = 2166136261
  for (let index = 0; index < name.length; index += 1) {
    hash ^= name.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length]!
}

/** A person's colours as the avatar draws them — for a circle that writes its own initials (Activity's inbox). */
export function avatarColours(name: string): { background: string; color: string } {
  const tone = avatarTone(name)
  return { background: `rgb(var(--status-job-${tone}-bg))`, color: `rgb(var(--status-job-${tone}-fg))` }
}

export function Avatar({ name, size = 30, self = false, tone: look, className, src }: AvatarProps) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part[0]!.toUpperCase()).join('')
  const tone = avatarTone(name)
  // A company is not a person, so it does not take a person's colour. The
  // tenant's circle is the accent, everywhere it is drawn.
  const accent = look === 'accent'

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold',
        accent && 'bg-accent-soft text-accent',
        !accent && self && 'bg-accent-ring text-accent-dark',
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.38 * 10) / 10,
        // The signed-in user keeps the accent ring §3.1 gives them: "this one
        // is you" is worth more than "this one is Priya".
        ...(self || accent ? {} : {
          background: `rgb(var(--status-job-${tone}-bg))`,
          color: `rgb(var(--status-job-${tone}-fg))`,
        }),
      }}
      title={name}
    >
      {/*
        * A picture when there is one, the initials when there is not. The
        * circle is the same size either way, so a list does not reflow as
        * photos are added one at a time.
        */}
      {src
        ? <img src={src} alt={name} className="h-full w-full object-cover" />
        : initials}
    </span>
  )
}

export interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  /**
   * Keep the label in the accessible name but out of the layout — the same
   * escape hatch `Checkbox` has, for the rows that draw their own text or
   * have none to spare.
   */
  hideLabel?: boolean
  disabled?: boolean
  /**
   * Draw the track, do not be the control.
   *
   * For a row that is itself the switch: a 34 × 20 target is right beside a
   * field label and wrong as the only thing to aim at on a 52px row where
   * every neighbour is tappable end to end, so those rows carry
   * `role="switch"` themselves and want this to say which way it is set.
   * It renders a `<span aria-hidden>` rather than a second `<button>` —
   * nesting one button inside another is invalid markup and puts a second
   * stop in the tab order for one control. `onChange` is not called.
   */
  readOnly?: boolean
  /**
   * `proposal` is the customer page's 46 × 28 switch in the company's colour
   * (`docs/proposal.md` board 4) — the same control at the size a customer's
   * thumb is aimed at, rather than a second switch component. A size prop and
   * not a class, because `cn` concatenates and two widths would leave the
   * answer to stylesheet order.
   */
  look?: 'app' | 'proposal' | 'proposal-touch'
  /**
   * `sm` is the visit page's 30 × 18 switch with a 14px knob (Board 8's
   * **Pay Sam back**, Boards 9 and 10) — opt-in, so every other switch keeps
   * the 34 × 20 below. Only with `look="app"`. `xs` is Board 9's 28 × 16
   * with a 12px knob beside **Tell the crew now** (`docs/visit-page.md` §13.2),
   * also opt-in.
   */
  size?: 'default' | 'sm' | 'xs'
}

/**
 * 34×20, accent when on (plan §3.6), with its label beside it.
 *
 * It used to put `label` in `aria-label` and nowhere else, which made a prop
 * that reads like "what this switch says" mean "what a screen reader says it
 * says". Half the call sites drew their own text next to it and the other half
 * trusted the prop — so a job's Billing tab, the payment settings and the
 * console each shipped a column of bare toggles with nothing to tell them
 * apart. Drawing it is the behaviour the name promises; `hideLabel` is for the
 * rows that genuinely have no room, and it is explicit at the call site.
 */
export function Switch({
  checked, onChange, label, hideLabel, disabled, readOnly, look = 'app', size = 'default',
}: SwitchProps) {
  const big = look !== 'app'
  const small = !big && size === 'sm'
  const tiny = !big && size === 'xs'
  /* 52 × 32 on a phone row (board 8), where the row is the target. */
  const touch = look === 'proposal-touch'
  const Track = readOnly ? 'span' : 'button'
  const control = (
    <Track
      {...(readOnly
        ? { 'aria-hidden': true }
        : {
          type: 'button' as const,
          role: 'switch',
          'aria-checked': checked,
          'aria-label': label,
          disabled,
          onClick: () => onChange(!checked),
        })}
      className={cn(
        'relative shrink-0 rounded-full transition-colors',
        touch ? 'h-8 w-[52px]' : big ? 'h-7 w-[46px]' : small ? 'h-[18px] w-[30px] disabled:opacity-50'
          : tiny ? 'h-4 w-7 disabled:opacity-50' : 'h-5 w-[34px] disabled:opacity-50',
        readOnly && disabled && !big && 'opacity-50',
        /* A locked proposal switch is a paler track rather than a faded one:
         * the knob stays white and solid, because "not yet" is not "broken". */
        checked ? (big ? 'bg-brand-ui' : 'bg-accent') : (big && disabled ? 'bg-line' : 'bg-line-strong'),
      )}
    >
      {/*
        * `left-0` is load-bearing. Without it the knob takes its static
        * position, and a button's text is centred, so the knob started 17px
        * in and the translate pushed it out of the track and over the label.
        */}
      <span
        className={cn(
          'absolute left-0 rounded-full bg-white transition-transform',
          touch
            ? cn('top-[3px] h-[26px] w-[26px] shadow-[0_1px_3px_rgba(0,0,0,0.25)]',
              checked ? 'translate-x-[23px]' : 'translate-x-[3px]')
            : big
            ? cn('top-[3px] h-[22px] w-[22px] shadow-[0_1px_3px_rgba(0,0,0,0.25)]',
              checked ? 'translate-x-[21px]' : 'translate-x-[3px]')
            : small
            ? cn('top-0.5 h-3.5 w-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.25)]',
              checked ? 'translate-x-[14px]' : 'translate-x-[2px]')
            : tiny
            ? cn('top-0.5 h-3 w-3 shadow-[0_1px_2px_rgba(0,0,0,0.25)]',
              checked ? 'translate-x-[14px]' : 'translate-x-[2px]')
            : cn('top-0.5 h-4 w-4', checked ? 'translate-x-[16px]' : 'translate-x-[2px]'),
        )}
      />
    </Track>
  )

  if (hideLabel) return control

  return (
    <span className="flex items-center gap-2.5">
      {control}
      <span className={cn('text-cell', disabled ? 'text-text-faint' : 'text-text')}>{label}</span>
    </span>
  )
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * What the box is called — and what it does, when that needs a second line.
   *
   * A node rather than a string, because `CheckRow` wants a name over a
   * consequence ("Show on proposal / customers see the line") and the
   * alternative was wrapping this component in a second `<label>`. Nesting
   * two labels is invalid markup, and it put the same words in the DOM twice:
   * once visible, once in the accessible name.
   */
  label?: ReactNode
  /** Keep the label in the accessible name but out of the layout. */
  hideLabel?: boolean
  /** Top-align the box, for a label that is two lines. */
  align?: 'center' | 'start'
  /** A group row where only some children are ticked (scope tree). */
  indeterminate?: boolean
}

export function Checkbox({
  label, hideLabel, indeterminate, align = 'center', className, ...props
}: CheckboxProps) {
  /*
   * `start` is also a block. A box with a two-line label is a row of its own,
   * and `inline-flex` ran three of them together on one line — "Taxable Show
   * on proposal Optional" with the subs underneath, which is not three
   * options, it is a sentence.
   */
  return (
    <label className={cn('gap-2 text-cell',
      align === 'start' ? 'flex items-start' : 'inline-flex items-center', className)}>
      <input
        type="checkbox"
        ref={(element) => {
          if (element) element.indeterminate = indeterminate ?? false
        }}
        className={cn(
          'h-4 w-4 shrink-0 appearance-none rounded-[4px] border border-line-strong bg-surface',
          'bg-center bg-no-repeat',
          'checked:border-accent checked:bg-accent',
          'indeterminate:border-accent indeterminate:bg-accent',
          /*
           * Every space in these data URIs is `%20`, and that is load-bearing.
           * A class attribute is split on whitespace, so a literal space inside
           * an arbitrary value tears the class into pieces and no rule is
           * generated — which is why no checkbox in the app had a tick in it.
           *
           * The dash matters as much as the tick: without its own mark an
           * indeterminate box was the same solid green as a checked one, so a
           * district with one branch selected read as the whole district being
           * selected — the opposite of what the tree is for.
           */
          `checked:bg-[url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2016%2016'%3E%3Cpath%20d='M3.5%208.5l3%203%206-6'%20fill='none'%20stroke='white'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'/%3E%3C/svg%3E")]`,
          `indeterminate:bg-[url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2016%2016'%3E%3Cpath%20d='M4%208h8'%20fill='none'%20stroke='white'%20stroke-width='2'%20stroke-linecap='round'/%3E%3C/svg%3E")]`,
          align === 'start' && 'mt-px',
        )}
        {...props}
      />
      {/*
        * A label with nowhere to go still has to be announced. `hideLabel`
        * keeps it in the accessible name and out of the layout, which is what
        * a tick box in a 36px table column needs — the visible version wrapped
        * "Select this row" onto three lines and pushed the whole table over.
        */}
      {label && <span className={cn(hideLabel && 'sr-only')}>{label}</span>}
    </label>
  )
}

export interface SegmentedProps<T extends string> {
  /** `disabled` keeps the option on the track, muted and `aria-disabled` (quote-build-refresh §6.5). */
  options: { value: T; label: string; glyph?: ReactNode; disabled?: boolean }[]
  value: T
  onChange: (value: T) => void
  label: string
  /** A `Field`'s `labelId`, when the group is named by the label above it. */
  labelledBy?: string
  describedBy?: string
  /** Fills the width it is given, each option an equal share of it. */
  full?: boolean
  /** `large` is the 30px track the user menu and the line editor draw. */
  /** `touch` is `field` at a phone's 40px track and 34px segments (`new-request.md` Board 14). */
  /**
   * `toolbar` is the visit page's 32px track with 26px segments touching
   * (`docs/visit-page.md` Board 7's *All days · Day 1 · Wed …*, radius 10/8,
   * 11px either side) — opt-in, so no other track moves.
   */
  size?: 'default' | 'large' | 'field' | 'touch' | 'toolbar'
}

/** `#ecebe7` track, white active with a 1px shadow (plan §3.6). */
export function Segmented<T extends string>({
  options, value, onChange, label, labelledBy, describedBy, full, size = 'default',
}: SegmentedProps<T>) {
  /*
   * `large` is the 30px track the mockups draw wherever a segmented control
   * sits beside a 14px label rather than inside a dense row — the user menu's
   * Appearance, the line editor's Source. `field` is the 36px one a record
   * form wears, so a segmented answer lines up with the text box above it
   * (`record-flyouts.md` §3). Same component, one number, so three sizes do
   * not become three controls.
   */
  const big = size === 'large'
  const touch = size === 'touch'
  const field = size === 'field' || touch
  const toolbar = size === 'toolbar'
  return (
    <div
      role="radiogroup"
      /*
       * Named by the label above it when there is one, and by its own word
       * otherwise. Both would read the name twice.
       */
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      className={cn('bg-segmented-track', !toolbar && 'gap-0.5',
        toolbar ? 'h-8 rounded-[10px] p-[3px]'
          : touch ? 'h-10 rounded-control p-[3px]'
          : field ? 'h-9 rounded-control p-[3px]'
          : big ? 'h-[30px] rounded-[9px] p-[3px]' : 'rounded-button p-0.5',
        full ? 'flex w-full' : 'inline-flex')}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          aria-disabled={option.disabled || undefined}
          onClick={() => { if (!option.disabled) onChange(option.value) }}
          className={cn(
            'font-semibold transition-colors',
            toolbar
              ? 'inline-flex h-[26px] items-center justify-center rounded-[8px] px-[11px] text-[12.5px]'
              : touch
              ? 'rounded-[7px] px-2.5 text-[14px]'
              : field
              ? 'rounded-[7px] px-2.5 text-[12.5px]'
              : big
                ? 'rounded-[7px] px-2.5 text-[12.5px]'
                : 'rounded-[6px] px-2.5 py-1 text-[11.5px]',
            full && 'flex flex-1 flex-col items-center justify-center gap-1',
            /*
             * A label that wraps makes the whole track taller than the search
             * box beside it. "No access" over two lines was doing exactly
             * that on the people list; every segmented control in the product
             * is a short word or two, so none of them wants to wrap.
             */
            'whitespace-nowrap',
            value === option.value
              ? 'bg-surface text-text shadow-segmented'
              : option.disabled
                ? 'cursor-default text-text-faint'
                : 'text-text-muted hover:text-text',
          )}
        >
          {option.glyph}
          {option.label}
        </button>
      ))}
    </div>
  )
}

// --- KVGrid, ProgressBar, Stepper, EmptyState ---------------------------

export function KVGrid({ rows }: { rows: { key: string; value: ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-[minmax(110px,auto)_1fr] gap-x-4 gap-y-2 text-cell">
      {rows.map((row) => (
        <div key={row.key} className="contents">
          <dt className="text-text-muted">{row.key}</dt>
          <dd className="m-0 text-text">{row.value}</dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * `tone` is what the bar is saying, not how full it is. A service line that is
 * overdue fills the bar exactly as a healthy one does, so a green bar beside
 * the words "overdue by 43,102 miles" said two opposite things at once.
 */
export function ProgressBar({
  value, max = 100, label, tone = 'ok',
}: {
  value: number
  max?: number
  label: string
  tone?: 'ok' | 'warn' | 'bad'
}) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100))
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      className="h-1.5 w-full overflow-hidden rounded-full bg-progress-track"
    >
      <div
        className={cn('h-full rounded-full transition-[width]',
          tone === 'bad' ? 'bg-danger' : tone === 'warn' ? 'bg-warn' : 'bg-accent')}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

export interface StepperProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  label: string
  /** 38px controls on touch surfaces (plan §3.6). */
  touch?: boolean
}

export function Stepper({ value, onChange, min = 0, max = 9999, step = 1, label, touch }: StepperProps) {
  const size = touch ? 'h-[38px] w-[38px]' : 'h-[30px] w-[30px]'
  return (
    <div className="inline-flex items-center rounded-button border border-line bg-surface" role="group" aria-label={label}>
      <button
        type="button"
        aria-label={`Less ${label}`}
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - step))}
        className={cn(size, 'text-text-muted disabled:opacity-40')}
      >
        −
      </button>
      {/*
        * Type-able, not just tappable. A crew closing out 691 sq ft of paver
        * is not going to press + six hundred and ninety-one times, and the
        * steppers exist for the ±1 correction, not for entry.
        */}
      <input
        type="text"
        inputMode="decimal"
        aria-label={label}
        value={value}
        onChange={(event) => {
          const next = Number(event.currentTarget.value.replace(/[^0-9.]/g, ''))
          if (Number.isFinite(next)) onChange(Math.min(max, Math.max(min, next)))
        }}
        onFocus={(event) => event.currentTarget.select()}
        className={cn(
          'tnum min-w-[42px] border-0 bg-transparent p-0 text-center font-semibold text-text',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring',
          touch ? 'w-[64px] text-body' : 'w-[42px] text-cell')}
      />
      <button
        type="button"
        aria-label={`More ${label}`}
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + step))}
        className={cn(size, 'text-text-muted disabled:opacity-40')}
      >
        +
      </button>
    </div>
  )
}

export interface EmptyStateProps {
  title: string
  body?: string
  action?: { label: string; onClick: () => void }
}

/** Plan §3.6 / inventory: one next-action button, never a dead end. */
export function EmptyState({ title, body, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      <p className="text-card text-text">{title}</p>
      {body && <p className="max-w-[46ch] text-cell text-text-muted">{body}</p>}
      {action && (
        <Button variant="primary" className="mt-1" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  )
}

/**
 * A band — `design-system.md` §3 and §4.2.
 *
 * The strip that says something about what is on the screen without being a
 * row of it: amber for what a change will do, teal for how a rule applies
 * here, green for what you will end up with. One component so the three tones
 * are three tokens rather than three hand-picked pairs of colours, and so a
 * band never turns into a shadowed card by accident.
 */
export function Band({ tone = 'amber', title, icon, children, className }: {
  tone?: 'amber' | 'teal' | 'green'
  /** The bold lead-in, said before the sentence rather than above it. */
  title?: string
  icon?: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <div
      role="note"
      className={cn('flex items-start gap-2 rounded-[11px] border px-3 py-2.5 text-[12.5px]',
        'leading-[1.45]',
        tone === 'amber' && 'border-amber-band-line bg-amber-band-bg text-amber-band-text',
        tone === 'teal' && 'border-teal-band-line bg-teal-band-bg text-teal-band-text',
        tone === 'green' && 'border-green-band-line bg-green-band-bg text-green-band-text',
        className)}
    >
      {icon && <span className="mt-px flex shrink-0">{icon}</span>}
      <span className="min-w-0">
        {title && <b className="font-bold">{title}</b>}
        {title && children ? ' ' : null}
        {children}
      </span>
    </div>
  )
}

export interface Stat {
  label: string
  /** Already formatted — "31.5 h", "$1,240", "—". */
  value: string
}

/**
 * Four or five numbers in a row — `design-system.md` §4.2.
 *
 * The gutter is a 22px pad and a hairline rather than a gap, so the rule sits
 * midway between two stats instead of hugging one; the last stat has no rule,
 * which is what stops the strip ending in a line pointing at nothing.
 *
 * Numbers are `tnum`: these change every few minutes on a running week, and
 * without tabular figures the whole row shifts sideways when 9 becomes 10.
 */
export function StatStrip({ stats, action, className }: {
  stats: Stat[]
  /** A button at the far right — "All my hours". */
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex items-center', className)}>
      {stats.map((stat, index) => (
        <div
          key={stat.label}
          className={cn('flex flex-col gap-0.5 pr-[22px]',
            index < stats.length - 1 && 'mr-[22px] border-r border-line-faint')}
        >
          <span className="text-[11px] text-text-muted">{stat.label}</span>
          <b className="tnum text-[18px] font-bold text-text">{stat.value}</b>
        </div>
      ))}
      {action && <span className="ml-auto">{action}</span>}
    </div>
  )
}
